import { Command } from 'commander';
import chalk from 'chalk';
import { spawn, ChildProcess } from 'child_process';
import path from 'path';
import { promises as fs } from 'fs';

interface ProcessInfo {
  name: string;
  process: ChildProcess;
  port: number;
  url: string;
}

// 실행 중인 프로세스들을 추적
const runningProcesses: ProcessInfo[] = [];

/**
 * 사용 가능한 포트 찾기
 */
async function findAvailablePort(startPort: number): Promise<number> {
  for (let port = startPort; port <= startPort + 10; port++) {
    try {
      const response = await fetch(`http://localhost:${port}/health`, {
        signal: AbortSignal.timeout(1000),
      });
      // 포트에서 응답이 오면 이미 사용 중
      continue;
    } catch (error) {
      // 연결 실패하면 사용 가능한 포트
      return port;
    }
  }
  throw new Error(`포트 ${startPort}-${startPort + 10} 범위에서 사용 가능한 포트를 찾을 수 없습니다.`);
}

/**
 * 서버가 준비될 때까지 대기
 */
async function waitForServer(port: number, maxAttempts = 60): Promise<boolean> {
  console.log(chalk.gray(`   ${port} 포트에서 서버 응답 대기 중...`));

  for (let i = 0; i < maxAttempts; i++) {
    try {
      // fetch를 사용해서 axios 의존성 제거
      const response = await fetch(`http://localhost:${port}/health`, {
        signal: AbortSignal.timeout(2000),
      });
      if (response.ok) {
        console.log(chalk.green(`   ✅ 포트 ${port} 준비 완료!`));
        return true;
      }
    } catch (error) {
      // 진행률 표시
      if (i > 0 && i % 15 === 0) {
        console.log(chalk.gray(`   아직 대기 중... (${i}/${maxAttempts}초)`));
      }
    }
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  console.log(chalk.red(`   ❌ 포트 ${port} 서버가 ${maxAttempts}초 후에도 준비되지 않았습니다.`));

  // 디버깅을 위해 수동으로 확인해보라고 안내
  console.log(chalk.yellow(`   🔍 수동 확인: curl http://localhost:${port}/health`));

  return false;
}

/**
 * 브라우저에서 URL 열기
 */
async function openBrowser(url: string): Promise<void> {
  try {
    // dynamic import로 open 모듈 로드
    const { default: open } = await import('open');
    await open(url);
  } catch (error) {
    console.log(chalk.yellow(`브라우저를 자동으로 열 수 없습니다. 수동으로 열어주세요: ${url}`));
  }
}

/**
 * 프로세스 정리 함수 설정
 */
function setupProcessCleanup(): void {
  const cleanup = () => {
    console.log(chalk.yellow('\n🧹 프로세스 정리 중...'));

    runningProcesses.forEach(({ name, process }) => {
      if (process && !process.killed) {
        console.log(chalk.gray(`  ❌ ${name} 종료 중...`));
        process.kill('SIGTERM');

        // 강제 종료를 위한 타이머
        setTimeout(() => {
          if (!process.killed) {
            process.kill('SIGKILL');
          }
        }, 5000);
      }
    });

    console.log(chalk.green('✅ 모든 프로세스가 정리되었습니다.'));
    process.exit(0);
  };

  process.on('SIGINT', cleanup);
  process.on('SIGTERM', cleanup);
  process.on('beforeExit', cleanup);
}

/**
 * 백엔드 서버 시작
 */
async function startBackendServer(): Promise<ProcessInfo> {
  console.log(chalk.blue('🚀 백엔드 서버 시작 중...'));

  // 1. 먼저 실제 backend 앱이 있는지 확인 (monorepo 내부)
  const backendAppPaths = [
    path.join(process.cwd(), 'apps/backend'),
    path.join(process.cwd(), '../apps/backend'),
    path.join(process.cwd(), '../../apps/backend'),
  ];

  for (const backendAppPath of backendAppPaths) {
    try {
      const packageJsonPath = path.join(backendAppPath, 'package.json');
      await fs.access(packageJsonPath);

      console.log(chalk.gray(`실제 Backend 앱을 실행: ${backendAppPath}`));

      // Backend 앱을 실행
      const backendProcess = spawn('pnpm', ['run', 'dev'], {
        cwd: backendAppPath,
        stdio: ['ignore', 'pipe', 'pipe'],
        env: {
          ...process.env,
          PORT: '4000',
          NODE_ENV: 'development',
        },
        shell: false,
      });

      const processInfo: ProcessInfo = {
        name: 'Backend Server (Dev)',
        process: backendProcess,
        port: 4000,
        url: 'http://localhost:4000',
      };

      // 백엔드 로그 처리
      backendProcess.stdout?.on('data', data => {
        const message = data.toString().trim();
        if (message && !message.includes('[nodemon]')) {
          console.log(chalk.gray(`[Backend] ${message}`));
        }
      });

      backendProcess.stderr?.on('data', data => {
        const message = data.toString().trim();
        if (message && !message.includes('warning')) {
          console.log(chalk.red(`[Backend Error] ${message}`));
        }
      });

      backendProcess.on('error', error => {
        console.error(chalk.red(`백엔드 서버 오류: ${error.message}`));
      });

      backendProcess.on('exit', code => {
        if (code !== 0 && code !== null) {
          console.log(chalk.yellow(`백엔드 서버가 종료되었습니다. 코드: ${code}`));
        }
      });

      runningProcesses.push(processInfo);
      return processInfo;
    } catch {
      // 이 경로에 backend 앱이 없음, 다음 경로 시도
      continue;
    }
  }

  // 2. 실제 backend 앱을 찾지 못한 경우, 번들된 서버 사용
  console.log(chalk.gray('실제 Backend 앱을 찾지 못했습니다. 번들된 서버를 사용합니다.'));

  const backendServerPaths = [
    path.join(process.cwd(), 'node_modules/debug-time-machine-cli/bundled/backend-server.js'),
    path.join(__dirname, '../bundled/backend-server.js'),
    path.join(__dirname, '../../bundled/backend-server.js'),
    path.join(__dirname, '../../../bundled/backend-server.js'),
  ];

  let backendServerPath: string | null = null;
  for (const p of backendServerPaths) {
    try {
      await fs.access(p);
      backendServerPath = p;
      console.log(chalk.gray(`번들된 Backend 서버 실행: ${p}`));
      break;
    } catch {
      // 파일이 없음
    }
  }

  if (!backendServerPath) {
    console.log(chalk.red('백엔드 서버 파일을 찾을 수 없습니다. 다음 경로들을 확인했습니다:'));
    backendServerPaths.forEach(p => console.log(chalk.gray(`  - ${p}`)));
    throw new Error('번들된 백엔드 서버를 찾을 수 없습니다.');
  }

  const backendProcess = spawn('node', [backendServerPath], {
    stdio: ['ignore', 'pipe', 'pipe'],
    env: { ...process.env, PORT: '4000' },
    shell: false,
  });

  const processInfo: ProcessInfo = {
    name: 'Backend Server (Bundled)',
    process: backendProcess,
    port: 4000,
    url: 'http://localhost:4000',
  };

  // 번들된 서버 로그 처리
  backendProcess.stdout?.on('data', data => {
    const message = data.toString().trim();
    if (message) {
      console.log(chalk.gray(`[Backend] ${message}`));
    }
  });

  backendProcess.stderr?.on('data', data => {
    const message = data.toString().trim();
    if (message && !message.includes('warning')) {
      console.log(chalk.red(`[Backend Error] ${message}`));
    }
  });

  backendProcess.on('error', error => {
    console.error(chalk.red(`백엔드 서버 오류: ${error.message}`));
  });

  backendProcess.on('exit', code => {
    if (code !== 0 && code !== null) {
      console.log(chalk.yellow(`백엔드 서버가 종료되었습니다. 코드: ${code}`));
    }
  });

  runningProcesses.push(processInfo);
  return processInfo;
}

/**
 * 프론트엔드 UI 시작
 */
async function startFrontendUI(): Promise<ProcessInfo> {
  console.log(chalk.blue('🎨 Debug UI 시작 중...'));

  // 1. 먼저 실제 frontend 앱이 있는지 확인 (monorepo 내부)
  const frontendAppPaths = [
    path.join(process.cwd(), 'apps/frontend'),
    path.join(process.cwd(), '../apps/frontend'),
    path.join(process.cwd(), '../../apps/frontend'),
  ];

  for (const frontendAppPath of frontendAppPaths) {
    try {
      const packageJsonPath = path.join(frontendAppPath, 'package.json');
      await fs.access(packageJsonPath);

      console.log(chalk.gray(`실제 Frontend 앱을 Vite로 실행: ${frontendAppPath}`));

      // Frontend 앱을 Vite로 실행
      const frontendProcess = spawn('pnpm', ['run', 'dev', '--port', '8080'], {
        cwd: frontendAppPath,
        stdio: ['ignore', 'pipe', 'pipe'],
        env: { ...process.env, PORT: '8080' },
        shell: false,
      });

      const processInfo: ProcessInfo = {
        name: 'Debug UI (Vite)',
        process: frontendProcess,
        port: 8080,
        url: 'http://localhost:8080',
      };

      // Vite 로그 처리
      frontendProcess.stdout?.on('data', data => {
        const message = data.toString().trim();
        if (message && !message.includes('ready in')) {
          console.log(chalk.gray(`[Debug UI] ${message}`));
        }
      });

      frontendProcess.stderr?.on('data', data => {
        const message = data.toString().trim();
        if (
          message &&
          !message.includes('warning') &&
          !message.includes('Local:') &&
          !message.includes('➜')
        ) {
          console.log(chalk.red(`[Debug UI Error] ${message}`));
        }
      });

      frontendProcess.on('error', error => {
        console.error(chalk.red(`Debug UI 오류: ${error.message}`));
      });

      frontendProcess.on('exit', code => {
        if (code !== 0 && code !== null) {
          console.log(chalk.yellow(`Debug UI가 종료되었습니다. 코드: ${code}`));
        }
      });

      runningProcesses.push(processInfo);
      return processInfo;
    } catch {
      // 이 경로에 frontend 앱이 없음, 다음 경로 시도
      continue;
    }
  }

  // 2. 실제 frontend 앱을 찾지 못한 경우, 번들된 서버 사용
  console.log(chalk.gray('실제 Frontend 앱을 찾지 못했습니다. 번들된 서버를 사용합니다.'));

  // 사용 가능한 포트 찾기
  let frontendPort = 8080;
  try {
    frontendPort = await findAvailablePort(8080);
    if (frontendPort !== 8080) {
      console.log(chalk.yellow(`포트 8080이 사용 중이므로 포트 ${frontendPort}을 사용합니다.`));
    }
  } catch (error) {
    console.log(chalk.red('사용 가능한 포트를 찾을 수 없습니다. 기본 포트 8080을 사용합니다.'));
    frontendPort = 8080;
  }

  const frontendServerPaths = [
    path.join(process.cwd(), 'node_modules/debug-time-machine-cli/bundled/frontend-server.js'),
    path.join(__dirname, '../bundled/frontend-server.js'),
    path.join(__dirname, '../../bundled/frontend-server.js'),
    path.join(__dirname, '../../../bundled/frontend-server.js'),
  ];

  let frontendServerPath: string | null = null;
  for (const p of frontendServerPaths) {
    try {
      await fs.access(p);
      frontendServerPath = p;
      console.log(chalk.gray(`번들된 Frontend 서버 실행: ${p}`));
      break;
    } catch {
      // 파일이 없음
    }
  }

  if (!frontendServerPath) {
    console.log(chalk.red('프론트엔드 서버 파일을 찾을 수 없습니다. 다음 경로들을 확인했습니다:'));
    frontendServerPaths.forEach(p => console.log(chalk.gray(`  - ${p}`)));
    throw new Error('번들된 프론트엔드 서버를 찾을 수 없습니다.');
  }

  const frontendProcess = spawn('node', [frontendServerPath], {
    stdio: ['ignore', 'pipe', 'pipe'],
    env: { ...process.env, PORT: frontendPort.toString() },
    shell: false,
  });

  const processInfo: ProcessInfo = {
    name: 'Debug UI (Bundled)',
    process: frontendProcess,
    port: frontendPort,
    url: `http://localhost:${frontendPort}`,
  };

  // 번들된 서버 로그 처리
  frontendProcess.stdout?.on('data', data => {
    const message = data.toString().trim();
    if (message) {
      console.log(chalk.gray(`[Debug UI] ${message}`));
    }
  });

  frontendProcess.stderr?.on('data', data => {
    const message = data.toString().trim();
    if (message && !message.includes('warning')) {
      console.log(chalk.red(`[Debug UI Error] ${message}`));
    }
  });

  frontendProcess.on('error', error => {
    console.error(chalk.red(`Debug UI 오류: ${error.message}`));
  });

  frontendProcess.on('exit', code => {
    if (code !== 0 && code !== null) {
      console.log(chalk.yellow(`Debug UI가 종료되었습니다. 코드: ${code}`));
    }
  });

  runningProcesses.push(processInfo);
  return processInfo;
}

/**
 * 사용자 앱 시작 (프록시 방식)
 */
async function startUserApp(command: string): Promise<ProcessInfo> {
  console.log(chalk.blue(`🎯 사용자 앱 시작: ${command}`));

  // 1. 먼저 실제 사용자 앱을 3000 포트에서 실행
  const parts = command.split(' ').filter(part => part.trim() !== '');
  const [cmd, ...args] = parts;

  const userAppProcess = spawn(cmd, args, {
    stdio: ['ignore', 'pipe', 'pipe'],
    shell: true,
    cwd: process.cwd(),
    env: {
      ...process.env,
      PORT: '3000', // 사용자 앱을 3000 포트로 고정
    },
  });

  // 사용자 앱 로그 처리
  userAppProcess.stdout?.on('data', data => {
    const message = data.toString().trim();
    if (message && !message.includes('webpack')) {
      console.log(chalk.gray(`[User App] ${message}`));
    }
  });

  userAppProcess.stderr?.on('data', data => {
    const message = data.toString().trim();
    if (message && !message.includes('warning')) {
      console.log(chalk.yellow(`[User App Error] ${message}`));
    }
  });

  userAppProcess.on('error', error => {
    console.error(chalk.red(`사용자 앱 오류: ${error.message}`));
  });

  userAppProcess.on('exit', code => {
    console.log(chalk.yellow(`사용자 앱이 종료되었습니다. 코드: ${code}`));
    // 사용자 앱이 종료되면 모든 프로세스 정리
    process.exit(code || 0);
  });

  // 2. 사용자 앱이 시작될 때까지 대기 (최대 30초)
  console.log(chalk.gray('   사용자 앱이 시작될 때까지 대기 중...'));
  
  let userAppReady = false;
  for (let i = 0; i < 30; i++) {
    try {
      const response = await fetch('http://localhost:3000', {
        signal: AbortSignal.timeout(2000),
      });
      if (response.ok || response.status < 500) {
        userAppReady = true;
        console.log(chalk.green('   ✅ 사용자 앱 준비 완료!'));
        break;
      }
    } catch {
      // 아직 준비되지 않음
    }
    
    if (i % 5 === 0 && i > 0) {
      console.log(chalk.gray(`   아직 대기 중... (${i}/30초)`));
    }
    
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  if (!userAppReady) {
    console.log(chalk.red('   ❌ 사용자 앱이 30초 후에도 준비되지 않았습니다.'));
    console.log(chalk.yellow('   수동으로 확인: curl http://localhost:3000'));
  }

  // 3. 프록시 서버 시작 (3001 포트)
  console.log(chalk.blue('🔄 Debug Time Machine 프록시 시작 중...'));
  
  const proxyServerPaths = [
    path.join(process.cwd(), 'node_modules/debug-time-machine-cli/bundled/inject-to-user-app.js'),
    path.join(__dirname, '../bundled/inject-to-user-app.js'),
    path.join(__dirname, '../../bundled/inject-to-user-app.js'),
    path.join(__dirname, '../../../bundled/inject-to-user-app.js'),
  ];

  let proxyServerPath: string | null = null;
  for (const p of proxyServerPaths) {
    try {
      await fs.access(p);
      proxyServerPath = p;
      console.log(chalk.gray(`프록시 서버 실행: ${p}`));
      break;
    } catch {
      // 파일이 없음
    }
  }

  if (!proxyServerPath) {
    console.log(chalk.red('프록시 서버 파일을 찾을 수 없습니다. 다음 경로들을 확인했습니다:'));
    proxyServerPaths.forEach(p => console.log(chalk.gray(`  - ${p}`)));
    throw new Error('프록시 서버를 찾을 수 없습니다.');
  }

  const proxyProcess = spawn('node', [proxyServerPath], {
    stdio: ['ignore', 'pipe', 'pipe'],
    env: { 
      ...process.env, 
      INJECTOR_PORT: '3001',
      USER_APP_PORT: '3000'
    },
    shell: false,
  });

  // 프록시 서버 로그 처리
  proxyProcess.stdout?.on('data', data => {
    const message = data.toString().trim();
    if (message) {
      console.log(chalk.gray(`[Proxy] ${message}`));
    }
  });

  proxyProcess.stderr?.on('data', data => {
    const message = data.toString().trim();
    if (message && !message.includes('warning')) {
      console.log(chalk.red(`[Proxy Error] ${message}`));
    }
  });

  proxyProcess.on('error', error => {
    console.error(chalk.red(`프록시 서버 오류: ${error.message}`));
  });

  proxyProcess.on('exit', code => {
    if (code !== 0 && code !== null) {
      console.log(chalk.yellow(`프록시 서버가 종료되었습니다. 코드: ${code}`));
    }
  });

  const processInfo: ProcessInfo = {
    name: 'User App with Debug Proxy',
    process: userAppProcess, // 주 프로세스는 사용자 앱
    port: 3001, // 사용자는 프록시 포트에 접속
    url: 'http://localhost:3001', // 프록시 URL
  };

  runningProcesses.push(processInfo);
  
  // 프록시 프로세스도 추가 (정리용)
  runningProcesses.push({
    name: 'Debug Proxy Server',
    process: proxyProcess,
    port: 3001,
    url: 'http://localhost:3001',
  });

  return processInfo;
}

/**
 * start-with-app 명령어 실행
 */
export async function startWithAppCommand(appCommand: string, options: any): Promise<void> {
  console.log(chalk.bold.green('🐛 Debug Time Machine 자동 시작!'));
  console.log(chalk.gray(`실행할 앱: ${appCommand}`));
  console.log('');

  try {
    // 프로세스 정리 설정
    setupProcessCleanup();

    // 1. 백엔드 서버 시작
    const backend = await startBackendServer();

    // 2. Debug UI 시작
    const frontend = await startFrontendUI();

    // 3. 서버들이 준비될 때까지 대기
    console.log(chalk.blue('⏳ 서버 준비 대기 중...'));

    const backendReady = await waitForServer(backend.port);
    if (!backendReady) {
      throw new Error('백엔드 서버가 준비되지 않았습니다.');
    }
    console.log(chalk.green('✅ 백엔드 서버 준비 완료'));

    const frontendReady = await waitForServer(frontend.port);
    if (!frontendReady) {
      throw new Error('Debug UI 서버가 준비되지 않았습니다.');
    }
    console.log(chalk.green('✅ Debug UI 서버 준비 완료'));

    // 4. Debug UI 자동으로 열기
    if (!options.noBrowser) {
      console.log(chalk.blue('🌐 Debug UI 브라우저에서 열기...'));
      await openBrowser(frontend.url);
    }

    // 5. 사용자 앱 시작
    console.log('');
    const userApp = await startUserApp(appCommand);

    // 6. 성공 메시지
    console.log('');
    console.log(chalk.bold.green('🎉 Debug Time Machine이 성공적으로 시작되었습니다!'));
    console.log('');
    console.log(chalk.bold('📍 서비스 주소:'));
    console.log(`  🎯 Your App:  ${chalk.cyan(userApp.url)} ${chalk.gray('(Debug 기능 자동 주입)')}`);
    console.log(`  🐛 Debug UI:  ${chalk.cyan(frontend.url)}`);
    console.log(`  🔧 Backend:   ${chalk.cyan(backend.url)}`);
    console.log('');
    console.log(chalk.bold('💡 중요 안내:'));
    console.log(`  • ${chalk.cyan('http://localhost:3001')} ${chalk.white('← 이 주소로 접속하세요!')}`);
    console.log(`  • Debug Time Machine이 자동으로 주입되어 Hook 없이도 이벤트가 캡처됩니다`);
    console.log(`  • 기존 http://localhost:3000은 원본 앱이므로 Debug 기능이 없습니다`);
    console.log('');
    console.log(chalk.gray('🔄 작동 원리: 사용자 앱(3000) → 프록시(3001) → 브라우저'));
    console.log(chalk.gray('⚠️  종료하려면 Ctrl+C를 누르세요.'));
    console.log('');

    // 프로세스를 계속 실행 상태로 유지
    await new Promise<void>(resolve => {
      // 무한 대기 대신 이벤트 기반으로 변경
      process.on('SIGINT', () => {
        resolve();
      });
      process.on('SIGTERM', () => {
        resolve();
      });
    });
  } catch (error) {
    console.error(chalk.red('❌ Error:'), error instanceof Error ? error.message : String(error));

    // 오류 발생 시 모든 프로세스 정리
    runningProcesses.forEach(({ process }) => {
      if (process && !process.killed) {
        process.kill('SIGTERM');
      }
    });

    process.exit(1);
  }
}

/**
 * Commander 명령어 생성
 */
export function createStartWithAppCommand(): Command {
  return new Command('start-with-app')
    .description('Debug Time Machine과 함께 앱을 자동으로 시작합니다')
    .argument('<command>', '실행할 앱 명령어 (예: "npm start", "yarn dev")')
    .option('--no-browser', 'Debug UI를 브라우저에서 자동으로 열지 않음')
    .action(startWithAppCommand);
}
